import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Avatar from "components/shared/Avatar";
import { cn } from "lib/utils";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { renderUserName } from "utils/helpers/user";
import { renderSettingsResourceLabel } from "utils/helpers/settings";
import { renderDuration } from "../../../../helpers/eventdisplay/renderDuration";
import { getResultShiftDetails } from "./helpers";
import type { SimpleTableColumn } from "components/shared/tables/SimpleTable";
import type { ShiftResult } from "./types";

export const useColumns = () => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();

    return useMemo<SimpleTableColumn<ShiftResult>[]>(() => [
        {
            header: t("employee"),
            cell: (row) => (
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Avatar user={row?.user} size="sm" />
                    <span>{renderUserName(row?.user)}</span>
                </div>
            ),
        },
        {
            header: t("date"),
            cell: (row) => {
                const { date } = getResultShiftDetails(row);
                return date ? moment(date).format(dateFormat) : "-";
            },
        },
        {
            header: t("time"),
            cell: (row) => {
                const { startDate, endDate } = getResultShiftDetails(row);

                if (!startDate || !endDate) {
                    return <span className="tw-text-neutral-400">-</span>;
                }

                return (
                    <span className="tw-px-3 tw-py-1 tw-rounded-lg tw-text-xs tw-font-semibold tw-bg-neutral-100 tw-text-neutral-700">
                        {moment.parseZone(startDate).format(timeFormat)}
                        {" - "}
                        {moment.parseZone(endDate).format(timeFormat)}
                    </span>
                );
            },
        },
        {
            header: t("duration"),
            cell: (row) => {
                const { duration } = getResultShiftDetails(row);
                return duration ? renderDuration(duration) : "-";
            },
        },
        {
            header: t("job"),
            cell: (row) => renderSettingsResourceLabel(getResultShiftDetails(row).job) || "-",
        },
        {
            header: t("location"),
            cell: (row) => renderSettingsResourceLabel(getResultShiftDetails(row).location) || "-",
        },
        {
            header: t("status"),
            cell: (row) => (
                <span
                    className={cn(
                        "tw-px-3 tw-py-1 tw-rounded-lg tw-text-xs tw-font-semibold",
                        row?.success
                            ? "tw-text-green-700 tw-bg-green-100"
                            : "shift-result-failed tw-text-rose-700 tw-bg-rose-100"
                    )}
                >
                    {t(row?.success ? "success" : "failed")}
                </span>
            ),
        },
        {
            header: t("message"),
            cell: (row) => (
                row?.message ? (
                    <span className={cn("tw-text-xs", !row?.success && "tw-text-rose-600")}>
                        {row.message}
                    </span>
                ) : (
                    <span className="tw-text-neutral-400">-</span>
                )
            ),
        },
    ], [t, dateFormat, timeFormat]);
};
